import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Table, Card, Button, Input, Select, Space,
  Typography, Row, Col, Popconfirm, Modal, message, Alert
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import BotonBloqueado from '../../components/BotonBloqueado';
import { useAuth } from '../../context/AuthContext';
import useColumnasResponsive from '../../components/tabla/useColumnasResponsive';
import { fecha, money } from '../../utils/format';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import TagCategoria from '../../components/TagCategoria';
import { animalService, categoriaService, eventoService, cabanaService } from '../../services/api';
import { eventosDeListaCorta } from '../../utils/listaCorta';

const { Title, Text } = Typography;
const { Option } = Select;

const AnimalesList = () => {
  const { tieneRecurso } = useAuth();
  /* Un solo eje para animales: el PLAN.
   *
   * En Eventos hay dos (propiedad y plan) porque el remate ajeno no se toca ni
   * con el mejor plan. Aca no: `Animal` es PERMEABLE_POR_INVITACION, asi que
   * todo lo que se ve es propio o gestionado, y en los dos casos `ensure_owner`
   * deja escribir. Lo unico que puede faltar es `crud_animales`. */
  const puedeEditar = tieneRecurso('crud_animales');
  /* Liberar va con SU recurso, y no con `crud_animales`.
   *
   * `liberar_evento()` esta gateado con `asignar_animales_evento`, que es una
   * entrada distinta del catalogo y encima de otro modulo (`eventos`, no
   * `animales`). Se togglean por membresia de forma INDEPENDIENTE: un plan con
   * `crud_animales` y sin `asignar_animales_evento` es perfectamente posible.
   *
   * Gatear con el recurso equivocado reproduce el patron destructivo que ya
   * conocemos: la pantalla te deja tildar 50 animales, confirmar, y recien ahi
   * te come el 403. El front tiene que prohibir lo mismo que el back. */
  const puedeLiberar = tieneRecurso('asignar_animales_evento');
  /* RETIRAR del remate (en curso o no) va con `crud_animales`: lo decide el
   * DUEÑO del animal —la cabaña invitada, que no asigna animales a ningún
   * remate pero saca el suyo si se golpeó— o el organizador sobre cualquier
   * animal de SU remate (2026-10-28). Con los dos recursos manda éste, que
   * cubre también los remates finalizados. */
  const puedeRetirar = puedeEditar;
  const puedeSacar = puedeRetirar || puedeLiberar;
  /* Asignar cabaña necesita DOS recursos, no uno.
   *
   * El endpoint pide `crud_animales`, pero el modal primero tiene que LISTAR
   * las cabañas, y `GET /cabanas/relacionadas` pide `ver_cabanas` — otra
   * entrada del catalogo, de otro modulo, que se togglea aparte.
   *
   * Sin este segundo chequeo, un plan con `crud_animales` y sin `ver_cabanas`
   * abre el modal, se come un 403 al cargar la lista, y queda con UNA sola
   * opcion en el Select: "Sin cabaña (desasignar)" — que el backend acepta,
   * porque el solo mira `crud_animales`. O sea que el camino degradado no
   * falla: ofrece un BORRADO MASIVO como unica accion posible. */
  const puedeAsignarCabana = puedeEditar && tieneRecurso('ver_cabanas');
  /* La columna de tildes aparece si HAY alguna accion masiva EJECUTABLE.
   *
   * Va contra `puedeAsignarCabana` y NO contra `puedeEditar`, que es lo mismo
   * pero sin `ver_cabanas`. Los tres recursos en juego —`crud_animales`,
   * `ver_cabanas` y `asignar_animales_evento`— son entradas independientes de
   * TRES modulos distintos del catalogo, asi que un plan puede traer el
   * primero y ninguno de los otros dos. Con `puedeEditar` ese plan veria la
   * columna de tildes, tildaria 50 animales y no le aparecería NINGUN boton:
   * la misma mentira que el 403 tardio, un paso antes. */
  const puedeSeleccionar = puedeSacar || puedeAsignarCabana;

  const [loading, setLoading] = useState(false);
  const [animales, setAnimales] = useState([]);
  /* Cuantos animales del tenant no tienen cabaña, en TODO el scope. Viene en
     cada respuesta del listado (`sin_cabana_total`), asi que se actualiza solo
     al asignar: el refresco que sigue al "Asignar cabaña" lo baja a cero sin
     ninguna request extra. */
  const [sinCabanaTotal, setSinCabanaTotal] = useState(0);
  const [categorias, setCategorias] = useState([]);
  const [eventos, setEventos] = useState([]);
  // Un `loading` por Select y no uno compartido: las dos requests son
  // independientes y una puede volver mucho antes que la otra. Sin esto el
  // dropdown se abre VACIO y hay que cerrarlo y reabrirlo cuando la API
  // contesta — y este archivo dispara TRES requests al montar, asi que la
  // ventana es real, no teorica.
  const [loadingCategorias, setLoadingCategorias] = useState(true);
  const [loadingEventos, setLoadingEventos] = useState(true);
  /* Contador para pedir un refresco de la grilla DESDE ADENTRO del efecto.
   * `handleDelete` llamaba a `fetchAnimales()` derecho, sin signal: esa request
   * quedaba invisible para el AbortController y nadie podia cancelarla. El
   * escenario concreto: borras un animal, el refresco sale, elegis una
   * categoria antes de que vuelva, el efecto aborta SOLO a su predecesora, y el
   * refresco sin filtrar aterriza ultimo. El Select dice "Toros" y la tabla
   * muestra todo. Es la misma carrera que el AbortController existe para
   * evitar: estaba la cerradura puesta y una segunda puerta abierta. */
  const [refresco, setRefresco] = useState(0);
  /* Animales tildados para liberar de su remate.
   *
   * Se limpia al liberar y NO al cambiar de pagina o de filtro, a proposito: el
   * caso real es "filtro por el remate viejo, selecciono todo, libero", y esa
   * seleccion tiene que sobrevivir al scroll. */
  const [seleccionados, setSeleccionados] = useState([]);
  /* De los tildados, los que ESTAN EN UN REMATE (`id_evento` no nulo). Es lo
     unico que "Liberar del remate" puede liberar, asi que el boton aparece y
     cuenta por esta lista y no por `seleccionados`. Antes contaba la seleccion
     entera: tildabas 5 animales sin remate y la barra ofrecia "Liberar del
     remate (5)", una accion que el backend iba a rechazar con 409. La misma
     mentira que un 403 tardio, un paso antes (2026-10-05).

     NO se deduce de `animales` al vuelo: la seleccion cruza paginas y las
     filas de otras paginas ya no estan en `dataSource`. antd las conserva
     (preserveSelectedRowKeys) y las entrega en `onChange`, que es donde se
     calcula. */
  const [seleccionadosEnRemate, setSeleccionadosEnRemate] = useState([]);
  const [liberando, setLiberando] = useState(false);
  // Asignación masiva de cabaña de procedencia.
  const [modalCabana, setModalCabana] = useState(false);
  const [cabanaElegida, setCabanaElegida] = useState(undefined);
  const [cabanas, setCabanas] = useState([]);
  // Mismo motivo que `loadingCategorias`/`loadingEventos`: sin esto el Select
  // se abre VACIO mientras la request viaja, y contra una base que esta en otra
  // region esa ventana no es teorica. Peor todavia acá, porque lo unico que se
  // ve en ese hueco es "Sin cabaña (desasignar)".
  const [cargandoCabanas, setCargandoCabanas] = useState(false);
  /* Flag de cache SEPARADO de `cabanas.length`. Usar el largo del array como
   * guarda confunde "todavia no cargue" con "cargue y no hay ninguna", asi que
   * el segundo caso vuelve a pedir la lista cada vez que se abre el modal. */
  const [cabanasCargadas, setCabanasCargadas] = useState(false);
  const [asignandoCabana, setAsignandoCabana] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    categoria: null,
    evento: null,
  });

  // Search debounced: no spamear el backend mientras el usuario tipea
  const debouncedSearch = useDebouncedValue(filters.search, 300);

  const navigate = useNavigate();
  /* `?sin_cabana=1` VIENE DE AFUERA: lo pone el aviso de la carga masiva
     ("cargaste N sin decir de que cabaña son" → "Vincular"). Vive en la URL y
     no en `filters` porque es un modo de llegada, no un filtro que el usuario
     arma aca: se entra con el, se tildan, se asigna, y el cartel lo saca. Y
     como URL, el productor puede volver mañana al mismo lugar desde el
     historial. Es el unico filtro de esta pantalla que se lee de la URL; los
     demas siguen en estado local como siempre. */
  const [searchParams, setSearchParams] = useSearchParams();
  const soloSinCabana = searchParams.get('sin_cabana') === '1';
  const salirDeSinCabana = () => {
    const siguiente = new URLSearchParams(searchParams);
    siguiente.delete('sin_cabana');
    setSearchParams(siguiente, { replace: true });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  useEffect(() => {
    // Las dos ya son concurrentes por ser async sin `await`; sumadas al
    // fetchAnimales del efecto siguiente son 3 requests al montar. El
    // `Promise.all` que envolvia esto era decorativo: cada una atrapa su
    // propio error, asi que la promesa combinada no podia rechazar, y nadie
    // la esperaba ni encadenaba nada.
    fetchCategorias();
    fetchEventos();
  }, []);

  useEffect(() => {
    // AbortController cancela fetches viejos cuando llega un cambio nuevo,
    // evitando que una respuesta atrasada sobrescriba el resultado actual.
    const controller = new AbortController();
    fetchAnimales(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, debouncedSearch, filters.categoria, filters.evento, refresco, soloSinCabana]);

  const fetchAnimales = async (signal) => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filters.categoria) params.categoria = filters.categoria;
      if (filters.evento) params.evento = filters.evento;
      if (soloSinCabana) params.sin_cabana = 1;

      const response = await animalService.getAll(params, signal);
      setAnimales(response.data?.animales || []);
      setSinCabanaTotal(response.data?.sin_cabana_total || 0);
      setPagination(prev => ({
        ...prev,
        total: response.data?.total || 0,
      }));
    } catch (error) {
      // Aborto deliberado por cambio de filtros: no es error, no toques nada
      if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') return;
      message.error(error.response?.data?.error || 'Error al cargar animales');
    } finally {
      /* EN `finally`, pero con guarda de aborto. Estaba suelto despues del
       * try/catch, y el `return` temprano del catch se lo saltaba: la unica
       * razon por la que no quedaba un spinner colgado era que React corre el
       * cleanup (el abort) ANTES del `setLoading(true)` del efecto entrante.
       * O sea, dependia del orden de fases de React, no de una decision.
       *
       * Y la guarda importa: un `finally` pelado apagaria el spinner mientras
       * la request NUEVA sigue viajando. Si nos abortaron, ya hay otro fetch
       * en curso con el spinner prendido — no es nuestro para apagarlo. */
      if (!signal?.aborted) setLoading(false);
    }
  };

  const fetchCategorias = async () => {
    try {
      // Solo categorias del tenant que tienen animales cargados → filtros
      // sin opciones vacias (UX mejorada).
      const response = await categoriaService.getConAnimales();
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.categorias || []);
      setCategorias(data);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar categorías');
    } finally {
      setLoadingCategorias(false);
    }
  };

  const fetchEventos = async () => {
    try {
      // lista-corta: solo 6 campos por evento (antes getActivos cargaba ~30
      // campos por evento, incluyendo imgsec_evento y mensaje_oferta_*).
      //
      // SIN `{estado: 1}`, y esto NO es un descuido: ese filtro devuelve
      // `estado_evento == 1 AND fecha_fin >= hoy`, y `liberar-evento` acepta
      // EXACTAMENTE lo contrario. Los dos conjuntos son DISJUNTOS, asi que
      // todo evento elegible en el filtro era uno cuyos animales el endpoint
      // iba a rechazar: el flujo "filtro por el remate viejo, selecciono todo,
      // libero" quedaba imposible desde la pantalla que lo ofrece.
      //
      // `GenerarPDF` y `Micrositio` SI mandan `{estado: 1}` y hacen bien — no
      // se publica el micrositio de un remate muerto. Esta pantalla es al
      // reves: existe para limpiar el pasado. Mismo parametro, requisito opuesto.
      const response = await eventoService.getListaCorta();
      setEventos(eventosDeListaCorta(response));
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar eventos');
    } finally {
      setLoadingEventos(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await animalService.delete(id);
      message.success('Animal eliminado');
      /* Sacarlo tambien de la seleccion. Si no, el boton sigue contandolo
       * ("Liberar del remate (3)") con un id que ya no existe: el backend no lo
       * encuentra en `tenant_scoped`, asi que no entra ni en `liberados` ni en
       * `ya_libres` y desaparece sin figurar en ningun contador. El cartel
       * prometia 3 y se liberan 2. */
      setSeleccionados(prev => prev.filter(sid => sid !== id));
      setSeleccionadosEnRemate(prev => prev.filter(sid => sid !== id));
      // Por el efecto, no llamando al fetcher: un solo camino de entrada, un
      // solo ciclo de vida, un solo controller.
      setRefresco(n => n + 1);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar');
    }
  };

  /* Saca los animales tildados del remate FINALIZADO en el que quedaron.
   *
   * POR QUE ESTA PANTALLA Y NO EL FORM DEL EVENTO. Sacar animales destildandolos
   * dentro del evento ya se podia, pero para eso hay que saber EN CUAL quedaron
   * y abrirlo. El productor que se topa con esto no esta ahi: esta mirando su
   * stock, o el asignador vacio de un remate nuevo, preguntandose donde estan
   * sus animales. Este es el lugar donde aparece la pregunta.
   *
   * El backend rechaza los de remates en curso y los devuelve por nombre; se
   * muestran en vez de tragarselos, porque "libere 40 de 50" sin decir cuales
   * quedaron afuera es peor que un error. */
  const handleLiberar = async () => {
    setLiberando(true);
    try {
      // Solo los que tienen remate: mandar la seleccion entera hacia que los
      // sin remate volvieran como `ya_libres`, un contador que no le dice nada
      // al productor porque nunca pidio liberarlos.
      const { data } = await animalService.liberarEvento(seleccionadosEnRemate);
      message.success(`${data.liberados} animales liberados`);
      /* EL EXITO PARCIAL SALE POR ACA, NO POR EL `catch`.
       *
       * El 409 sale solo cuando NO se libero NINGUNO. El caso mixto —40 de
       * remates cerrados y 10 de uno en curso— vuelve 200 con
       * `en_remate_activo` poblado. Si esto se lee solo en el catch, esos 10
       * se evaporan: el productor lee "40 liberados", va al asignador y le
       * faltan 10 sin saber cuales ni por que. */
      if (data.en_remate_activo?.length) {
        message.warning(
          `Quedaron afuera: ${data.en_remate_activo.join(', ')} están en un remate ` +
          'en curso. Para sacarlos de ahí, editá ese remate.'
        );
      }
      setSeleccionados([]);
      setSeleccionadosEnRemate([]);
      setRefresco(n => n + 1);
    } catch (error) {
      const d = error.response?.data;
      if (d?.en_remate_activo?.length) {
        message.warning(
          `No se liberaron: ${d.en_remate_activo.join(', ')} están en un remate en curso. ` +
          'Para sacarlos de ahí, editá ese remate.'
        );
      } else if (d?.ya_libres) {
        message.info('Esos animales ya estaban libres, no pertenecen a ningún remate.');
      } else {
        /* El backend distingue "tu plan no lo incluye" de "se te vencio la
         * suscripcion" a proposito: son dos conversaciones comerciales
         * distintas. Aplastarlas en un error generico tira esa distincion. */
        message.error(d?.error || 'Error al liberar los animales');
      }
    } finally {
      /* En `finally` y no suelto despues del try/catch: si el `join` o el
       * propio `message` tiran, el boton queda en loading para siempre y no
       * hay forma de reintentar sin recargar. Misma razon que en
       * `fetchAnimales`, donde ya esta explicada. */
      setLiberando(false);
    }
  };

  /* Saca los animales tildados de su remate, esté EN CURSO o no. El backend
   * decide animal por animal (mío, o en un remate mío) y devuelve por nombre
   * los que no se pueden; el caso mixto es 200 con `rechazados`. */
  const handleRetirar = async () => {
    setLiberando(true);
    try {
      const { data } = await animalService.retirarEvento(seleccionadosEnRemate);
      message.success(`${data.retirados} animal(es) retirados del remate`);
      if (data.con_ofertas) {
        message.warning(
          `${data.con_ofertas} de ellos tenían ofertas en ese remate. Las ofertas quedan ` +
          'registradas, pero el animal ya no está en venta.'
        );
      }
      if (data.rechazados?.length) {
        message.warning(`No se pudieron retirar: ${data.rechazados.join(', ')} (no son tuyos ni están en un remate tuyo).`);
      }
      setSeleccionados([]);
      setSeleccionadosEnRemate([]);
      setRefresco(n => n + 1);
    } catch (error) {
      const d = error.response?.data;
      if (d?.rechazados?.length) {
        message.warning(`No se retiraron: ${d.rechazados.join(', ')} (no son tuyos ni están en un remate tuyo).`);
      } else {
        message.error(d?.error || 'Error al retirar los animales');
      }
    } finally {
      setLiberando(false);
    }
  };

  /* Abre el modal de cabaña y carga la lista la PRIMERA vez.
   *
   * Perezoso a proposito: este archivo ya dispara tres requests al montar y las
   * cabañas solo hacen falta si el usuario va a usar esta accion, que no es la
   * habitual.
   *
   * VA `getRelacionadas` Y NO `getActivas` por el PAYLOAD, no por el alcance:
   * las dos scopean con `tenants_gestionados()`, asi que las dos ya traen las
   * cabañas invitadas. Lo que `activas` no trae es `duenio` ni `es_propia`
   * —devuelve `Cabana.to_dict()` pelado— y sin eso la etiqueta no puede
   * distinguir una cabaña propia de una consignada.
   *
   * El precio de esa eleccion es que `relacionadas` NO filtra por
   * `estado_cabana`, cosa que `activas` si hace. Por eso se filtran acá: una
   * cabaña dada de baja no puede ofrecerse como procedencia. */
  const abrirModalCabana = async () => {
    setCabanaElegida(undefined);

    /* EL CHEQUEO DEL VACIO VA ANTES DEL ATAJO DE CACHE, y esto es el corazon de
     * la funcion. Si el atajo sale primero, el segundo click abre el modal con
     * la lista vacia y el Select queda otra vez con "Sin cabaña (desasignar)"
     * como unica opcion — el borrado masivo a un click, por el camino que la
     * primera pasada acababa de cerrar. */
    if (cabanasCargadas) {
      if (!cabanas.length) {
        message.info('No tenés ninguna cabaña activa. Creá una en la sección Cabañas.');
        return;
      }
      setModalCabana(true);
      return;
    }

    setModalCabana(true);
    setCargandoCabanas(true);
    try {
      const { data } = await cabanaService.getRelacionadas();
      const activas = (data?.cabanas || data || []).filter(c => c.estado_cabana === 1);
      setCabanas(activas);
      /* UNA LISTA VACIA ES UN 200, NO UN ERROR, y termina en el mismo lugar que
       * el fallo. Que el estado final sea inaceptable no depende de por que
       * camino se llego.
       *
       * Y NO SE CACHEA el vacio: el usuario puede irse a crear una cabaña y
       * volver, asi que "no hay ninguna" es un estado que cambia. Cachearlo
       * dejaria la accion muerta hasta recargar la pagina. */
      if (!activas.length) {
        message.info('No tenés ninguna cabaña activa. Creá una en la sección Cabañas.');
        setModalCabana(false);
        return;
      }
      setCabanasCargadas(true);
    } catch (error) {
      message.error(error.response?.data?.error || 'No pudimos cargar las cabañas');
      /* Mismo motivo que el vacio de arriba. `puedeAsignarCabana` ya cubre el
       * caso de permisos; esto cubre el resto —una caida de red, un 500— donde
       * el gate no puede saber nada de antemano. */
      setModalCabana(false);
    } finally {
      setCargandoCabanas(false);
    }
  };

  /* Asigna la cabaña de PROCEDENCIA a los animales tildados.
   *
   * `cabanaElegida === null` significa DESASIGNAR, y por eso el chequeo mira
   * `undefined` y no un falsy: `null` es una eleccion valida del usuario y
   * `0` nunca es un id real, pero confiar en eso es como pedirlo. */
  const handleAsignarCabana = async () => {
    if (cabanaElegida === undefined) {
      message.warning('Elegí una cabaña');
      return;
    }
    setAsignandoCabana(true);
    try {
      const { data } = await animalService.asignarCabanaMasivo(seleccionados, cabanaElegida);
      message.success(`${data.actualizados} animales actualizados`);
      setModalCabana(false);
      setSeleccionados([]);
      setSeleccionadosEnRemate([]);
      setRefresco(n => n + 1);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al asignar la cabaña');
    } finally {
      setAsignandoCabana(false);
    }
  };

  const handleTableChange = (pag) => {
    setPagination(prev => ({
      ...prev,
      current: pag.current,
      pageSize: pag.pageSize,
    }));
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const columns = [
    {
      title: 'Corral',
      dataIndex: 'corral',
      key: 'corral',
      width: 80,
      /* SIN `sorter`. Lo tenia en `true`, que en una tabla paginada por el
       * servidor significa "el backend ordena, avisame por onChange". No
       * ordenaba nada: `handleTableChange` recibe solo el primer argumento
       * —antd pasa (pagination, filters, sorter, extra)— y ademas
       * `animal_routes.get_animales` no acepta ningun parametro de orden:
       * hardcodea `order_by(corral, nom_animal, id_animal)`. La flechita
       * giraba y las filas volvian igual. Una afordancia que miente es peor
       * que no tenerla; si hace falta ordenar, se agrega en el backend. */
    },
    {
      title: 'Nombre',
      dataIndex: 'nom_animal',
      key: 'nom_animal',
      render: (text, record) => (
        <a onClick={() => navigate(`/animales/${record.id_animal}`)}>{text}</a>
      ),
    },
    {
      title: 'RP',
      dataIndex: 'rp_animal',
      key: 'rp_animal',
      width: 100,
    },
    {
      title: 'Categoría',
      key: 'categoria',
      render: (_, record) => <TagCategoria valor={record.categoria?.nom_categoria} />,
    },
    {
      title: 'Fecha Nac.',
      dataIndex: 'fecha_nac',
      key: 'fecha_nac',
      width: 120,
      // Sin `render` se mostraba el ISO crudo del backend.
      render: (v) => fecha(v),
    },
    {
      title: 'Precio Base',
      dataIndex: 'precio_base',
      key: 'precio_base',
      width: 120,
      // `toLocaleString()` pelado muestra hasta TRES decimales, mientras que el
      // Excel y el PDF redondean a cero: el mismo numero con dos caras. Es el
      // bug que `money()` existe para evitar, documentado en utils/format.js.
      render: (v) => money(v),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      // `size={2}` y no `small` (8): en mobile esta columna le competia el ancho
      // a la celda apilada.
      //
      // LOS BOTONES SIGUEN MIDIENDO 32px, y no se achican a `small` (24px)
      // aunque tengan aire: ese aire —8px alrededor de un icono de 16— es
      // justo lo que hace que el dedo no falle, y uno de los tres BORRA. El
      // ancho que hacia falta se saco del padding del Card, que no le cuesta
      // nada a nadie.
      render: (_, record) => (
        <Space size={2}>
          <Button 
            type="text" 
            icon={<EyeOutlined />} 
            onClick={() => navigate(`/animales/${record.id_animal}`)}
          />
          {/* ACA EL UNICO EJE ES EL PLAN, no la propiedad, y no es un olvido.
              `Animal` es PERMEABLE_POR_INVITACION: el titular de un remate
              multi-cabania EDITA los animales que le consignaron —le pone
              precio base, le carga la venta— y por eso `ensure_owner` los
              acepta. O sea que todo lo que aparece en esta lista es tuyo o lo
              gestionas vos: si lo ves, a nivel propiedad podes tocarlo.
              Lo unico que puede faltar es el recurso. */}
          {puedeEditar ? (
            <>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => navigate(`/animales/${record.id_animal}/editar`)}
              />
              <Popconfirm
                title="¿Eliminar este animal?"
                onConfirm={() => handleDelete(record.id_animal)}
                okText="Sí"
                cancelText="No"
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </>
          ) : (
            <>
              <BotonBloqueado motivo="plan" type="text" icon={<EditOutlined />} />
              <BotonBloqueado motivo="plan" type="text" danger icon={<DeleteOutlined />} />
            </>
          )}
        </Space>
      ),
    },
  ];

  /* Vista mobile: dos columnas, el nombre arriba y el resto apilado abajo.
   * El `render` del nombre (que lo hace clickeable) y el `<Tag>` de categoria
   * se reusan tal cual — el helper referencia las columnas por su `key`. */
  const { columnas, propsTabla } = useColumnasResponsive({
    desktop: columns,
    scroll: { x: 'max-content' },
    mobile: {
      title: 'Datos',
      principal: 'nom_animal',
      secundarios: [
        // Corral y RP: dos identificadores sueltos, sin etiqueta no se sabe
        // cual es cual. Categoria y precio se explican solos —un <Tag> de
        // color y un numero con signo $—, asi que van pelados.
        //
        // LA CATEGORIA VA ULTIMA aunque en desktop este en el medio: es el
        // unico dato que se dibuja adentro de un contenedor con color, y en el
        // medio le partia el bloque en dos. Al final, el texto plano queda
        // junto y el chip cierra la celda.
        { key: 'corral', etiqueta: 'Corral' },
        { key: 'rp_animal', etiqueta: 'RP' },
        { key: 'fecha_nac', etiqueta: 'F.Nac' },
        { key: 'precio_base' },
        { key: 'categoria' },
      ],
      // Tres botones de icono entran en ~112px. Sin declararlo, el navegador
      // repartia mitad y mitad y le comia el ancho a lo que importa.
      conservar: [{ key: 'actions', width: 120 }],
    },
  });

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space size={12} wrap align="center">
            <Title level={2} style={{ margin: 0 }}>Animales</Title>
            {/* EL CONTADOR, al lado del titulo, SIEMPRE que haya alguno. El
                modal de la carga masiva avisa UNA vez y solo por ese camino;
                esto avisa cada vez que se entra, venga de donde venga el
                animal (formulario, CSV viejo, desasignacion). Se esconde en
                el modo "sin cabaña", donde el cartel de abajo ya lo dice, y
                si el plan no puede asignar: un numero sin accion es ruido. */}
            {sinCabanaTotal > 0 && !soloSinCabana && puedeAsignarCabana && (
              <Space size={8} wrap>
                <Text type="warning" style={{ fontWeight: 500 }}>
                  Tenés {sinCabanaTotal} animal{sinCabanaTotal === 1 ? '' : 'es'} sin cabaña
                </Text>
                <Button size="small" onClick={() => navigate('/animales?sin_cabana=1')}>
                  Ver animales
                </Button>
              </Space>
            )}
          </Space>
        </Col>
        <Col>
          <Space>
            {/* Solo con algo tildado: un boton de accion masiva permanente y
                siempre deshabilitado es ruido en la barra. Aparece cuando tiene
                sentido, y dice CUANTOS para que nadie libere 200 creyendo que
                eran 2. */}
            {seleccionados.length > 0 && puedeAsignarCabana && (
              <Button onClick={abrirModalCabana}>
                Asignar cabaña ({seleccionados.length})
              </Button>
            )}
            {/* Solo si entre los tildados hay alguno EN un remate, y cuenta
                esos. Un animal sin remate no se puede liberar de nada, y un
                animal sin cabaña puede perfectamente estar en uno (los 309
                de Pilaga lo estuvieron), asi que la regla es por `id_evento`
                y no por el modo de la pantalla. */}
            {seleccionadosEnRemate.length > 0 && puedeRetirar && (
              <Popconfirm
                title="Sacar del remate"
                description={
                  `Vas a sacar ${seleccionadosEnRemate.length} animal(es) del remate en el que están, ` +
                  'aunque esté en curso: dejan de verse en el micrositio y quedan libres para ' +
                  'otro remate. Las ofertas y ventas ya registradas no se tocan.'
                }
                okText="Sacar"
                cancelText="Cancelar"
                onConfirm={handleRetirar}
              >
                <Button danger loading={liberando}>
                  Sacar del remate ({seleccionadosEnRemate.length})
                </Button>
              </Popconfirm>
            )}
            {/* Sin `crud_animales` pero con `asignar_animales_evento`: queda
                la limpieza de remates FINALIZADOS de siempre. */}
            {seleccionadosEnRemate.length > 0 && !puedeRetirar && puedeLiberar && (
              <Popconfirm
                title="Liberar del remate"
                description={
                  `Vas a sacar ${seleccionadosEnRemate.length} animal(es) del remate finalizado ` +
                  'en el que están, para poder consignarlos a otro. Las ofertas y ventas ' +
                  'del remate anterior no se tocan.'
                }
                okText="Liberar"
                cancelText="Cancelar"
                onConfirm={handleLiberar}
              >
                <Button danger loading={liberando}>
                  Liberar del remate ({seleccionadosEnRemate.length})
                </Button>
              </Popconfirm>
            )}
            {/* Cada boton con SU recurso: carga masiva es una feature aparte
                del alta de a uno, y hay planes que traen una y no la otra. */}
            {tieneRecurso('carga_masiva_animales') ? (
              <Button
                icon={<UploadOutlined />}
                onClick={() => navigate('/animales/carga-masiva')}
              >
                Carga Masiva
              </Button>
            ) : (
              <BotonBloqueado motivo="plan" icon={<UploadOutlined />}>
                Carga Masiva
              </BotonBloqueado>
            )}
            {puedeEditar ? (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/animales/nuevo')}
              >
                Nuevo Animal
              </Button>
            ) : (
              <BotonBloqueado motivo="plan" type="primary" icon={<PlusOutlined />}>
                Nuevo Animal
              </BotonBloqueado>
            )}
          </Space>
        </Col>
      </Row>

      {/* `card-tabla`: en mobile le baja el padding del cuerpo de 24px a 12px.
          Son 24px de los 48 que el Card se lleva en aire, y en 360px eso es el
          13% de la pantalla. Ver la regla en index.css. */}
      <Card className="card-tabla">
        {/* El modo "sin cabaña", con su salida. Se dibuja ARRIBA de los
            filtros porque los recorta a todos: sin el cartel, el productor
            busca "Pomelo", no lo encuentra, y no hay nada en pantalla que
            diga por que. */}
        {soloSinCabana && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Mostrando solo los animales que no tienen cabaña"
            description="Sin cabaña no entran al catálogo de genética. Tildalos y usá «Asignar cabaña»."
            action={<Button size="small" onClick={salirDeSinCabana}>Ver todos</Button>}
          />
        )}
        {/* Filtros */}
        {/* gutter EN ARRAY: `gutter={16}` es solo horizontal, el vertical queda en 0.
            En desktop los tres filtros van en una fila y no se nota; en mobile
            `xs={24}` los apila y quedaban pegados, sin un pixel de separacion. */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Input
              placeholder="Buscar por nombre o RP..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={8}>
            <Select
              placeholder="Filtrar por categoría"
              style={{ width: '100%' }}
              loading={loadingCategorias}
              allowClear
              value={filters.categoria}
              onChange={(value) => updateFilter('categoria', value)}
            >
              {categorias.map(cat => (
                <Option key={cat.id_categoria} value={cat.id_categoria}>
                  {cat.nom_categoria}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8}>
            <Select
              placeholder="Filtrar por evento"
              style={{ width: '100%' }}
              loading={loadingEventos}
              allowClear
              value={filters.evento}
              onChange={(value) => updateFilter('evento', value)}
            >
              {eventos.map(ev => (
                <Option key={ev.id_evento} value={ev.id_evento}>
                  {/* `es_finalizado` LO DECIDE EL BACKEND, que es donde vive la
                      regla (`Evento.es_finalizado`). Recalcularlo aca con
                      `estado_evento` + `fecha_fin` divergia: el back corre en
                      UTC y el navegador en UTC-3. */}
                  {ev.nombre_evento}{ev.es_finalizado ? ' (finalizado)' : ''}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        {/* Tabla */}
        <Table
          {...propsTabla}
          columns={columnas}
          dataSource={animales}
          rowKey="id_animal"
          loading={loading}
          /* Aparece si hay ALGUNA accion masiva EJECUTABLE: liberar
             (`asignar_animales_evento`) o asignar cabaña (`crud_animales` Y
             `ver_cabanas`, las dos). Ver el bloque de `puedeAsignarCabana`
             arriba, que es donde esta explicado por que son dos y no una. */
          rowSelection={puedeSeleccionar ? {
            selectedRowKeys: seleccionados,
            /* `rows` trae TODAS las filas tildadas, tambien las de otras
               paginas (es lo que conserva `preserveSelectedRowKeys`): de ahi
               sale cuantas estan en un remate. */
            onChange: (keys, rows) => {
              setSeleccionados(keys);
              setSeleccionadosEnRemate(rows.filter((r) => r?.id_evento).map((r) => r.id_animal));
            },
            /* SIN ESTO LA SELECCION NO CRUZA LAS PAGINAS, y esta tabla pagina
               del lado del SERVIDOR: al pasar a la pagina 2 las filas de la 1
               ya no estan en `dataSource`. antd, en cada cambio, filtra las
               claves contra el dataSource actual y descarta las que no
               encuentra (`useSelection.js`, "Filter key which not exist in the
               dataSource"). O sea: tildabas 20 en la primera pagina, uno mas en
               la segunda, y el contador caia a 1 EN SILENCIO — se liberaba 1 en
               vez de 21. Es exactamente el caso de Pilaga: 309 animales, 16
               paginas. */
            preserveSelectedRowKeys: true,
            /* Se deshabilita la fila SOLO si la unica accion disponible es
               liberar y el animal no esta en ningun remate: ahi no hay nada de
               donde sacarlo, y tildarlo seria una promesa vacia.
               Con `crud_animales` la cosa cambia, porque asignar cabaña aplica
               a CUALQUIER animal — de hecho los recien liberados, que no tienen
               remate, son el caso principal.
               Va `puedeAsignarCabana` y no `puedeEditar`: sin `ver_cabanas` esa
               accion no se puede ejecutar, asi que habilitar la fila por ella
               seria ofrecer algo que no existe. */
            getCheckboxProps: (r) => ({ disabled: !puedeAsignarCabana && !r.id_evento }),
          } : undefined}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} animales`,
          }}
          onChange={handleTableChange}
        />
      </Card>

      <Modal
        title={`Asignar cabaña a ${seleccionados.length} animal(es)`}
        open={modalCabana}
        onOk={handleAsignarCabana}
        onCancel={() => setModalCabana(false)}
        okText="Asignar"
        cancelText="Cancelar"
        confirmLoading={asignandoCabana}
      >
        <p style={{ marginTop: 0 }}>
          La cabaña marca de <strong>qué unidad productiva viene</strong> el animal.
          Es lo que permite que un remate de varias cabañas distinga la procedencia
          de cada lote, y lo que se nombra en la columna CABAÑA del CSV.
        </p>
        <Select
          style={{ width: '100%' }}
          placeholder="Elegí una cabaña"
          loading={cargandoCabanas}
          disabled={cargandoCabanas}
          value={cabanaElegida}
          onChange={setCabanaElegida}
          options={[
            /* "Sin cabaña" es una opcion EXPLICITA y no el vacio del Select:
               desasignar es una decision del usuario, y tiene que poder tomarla
               sin que se confunda con "todavia no elegi nada". */
            { value: null, label: 'Sin cabaña (desasignar)' },
            /* Las ajenas se etiquetan con su dueña. `getRelacionadas` trae las
               propias Y las de quienes consignan en mis remates, y dos cabañas
               pueden tener nombres parecidos: sin el dueño, el titular no sabe
               cual es cual justo cuando esta marcando procedencia. */
            ...cabanas.map(c => ({
              value: c.id_cabana,
              label: [
                c.nom_cabana,
                c.alias ? `(${c.alias})` : null,
                c.es_propia ? null : `— ${c.duenio}`,
              ].filter(Boolean).join(' '),
            })),
          ]}
        />
      </Modal>
    </div>
  );
};

export default AnimalesList;